import Cookies from "js-cookie";
import jwt from "jwt-decode";
import jwtDecode from "jwt-decode";
import { login } from "../api/authentication/authentication";
import { DialogSelected } from "../constant/common";
import { environment } from "../constant/environment";
import { Messages } from "../constant/messages/toastMessages";
import { toastMessage } from "../helpers/toast/toastMessage";
import { getItemFromLocalStorage, setItemInLocalStorage } from "../hooks/localStorage";
import { LoginResponse } from "../interface";
import { UserInfo } from "../types";
import type { iGlobalContext } from "../context/globalContext/interface";
import { decryptData } from "./api/encryption";
import { isValidJWT } from "./authentication/isValidJWT";
import { isEmbedMode } from "./embedMode";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../constant/google-analytics/GAEvents";
import { consoleLog } from "./consoleLog/consoleLog";

// Get JWT from local storage
export function checkIfUserIsLoggedIn(setIsValidAccessToken: any, setUserInfo: (userInfo: UserInfo) => void): void {
  // In an embed frame only the portal (SSO post / ticket) may set who this is;
  // the token stored there is the operator's portal token, not the traveller's.
  if (isEmbedMode()) return;
  const userJWT = getItemFromLocalStorage("_zenvoya_JWT");

  if (userJWT && isValidJWT(userJWT)) {
    const decryptedToken = decryptData(userJWT);
    const decodedToken: any = jwtDecode(decryptedToken);
    setIsValidAccessToken(true);
    setUserInfo({
      id: decodedToken.id,
      name: decodedToken.name,
      email: decodedToken.email,
      phone: "",
      profile_pic: "",
      loggedIn: true,
      role: decodedToken?.role || 0,
    });
    /**
     * post message data
     */
    if (window && window.parent) {
      const message = {
        action: "CheckIfUserIsLoggedIn",
        data: decodedToken,
      };
      window.parent.postMessage(JSON.stringify(message)); // or specify a specific origin instead of "*"
    }
  } else {
    setIsValidAccessToken(false);
    setItemInLocalStorage("_zenvoya_JWT", "");
    setUserInfo({
      id: "",
      name: "",
      email: "",
      phone: "",
      profile_pic: "",
      loggedIn: false,
      role: 0,
      preferences: {},
    });
  }
}

type LoginSessionSetters = Pick<
  iGlobalContext,
  "setIsValidAccessToken" | "setUserInfo" | "setIsLoggedIn" | "setAccessJWT"
>;

export function storeLoginSession(
  loginData: any,
  { setIsValidAccessToken, setUserInfo, setIsLoggedIn, setAccessJWT }: LoginSessionSetters
) {
  const decodedToken: any = jwt(decryptData(loginData?.jwtToken) || loginData);
  consoleLog(["LOG IN CHECK LOGIN TOKEN", decodedToken]);
  setIsValidAccessToken(true);
  const userInfoData = {
    id: decodedToken.id,
    name: decodedToken.name,
    email: decodedToken.email,
    existingUser: loginData?.existingUser,
    loggedIn: true,
    loginType: loginData?.loginType,
  };
  setUserInfo((prev: any) => ({
    ...prev,
    ...userInfoData,
  }));
  setIsLoggedIn(true);

  if (window && window.parent) {
    const message = {
      action: "LogIn",
      data: userInfoData,
    };
    const iframe: HTMLIFrameElement | null = document.querySelector("#app_domain");
    const wind = iframe?.contentWindow;
    const data = {
      ...userInfoData,
      jwt: loginData?.jwtToken || loginData,
    };
    Cookies.set(`${environment}userData`, JSON.stringify(data), {
      path: "/",
      expires: 700,
      domain: ".zenvoya.ai",
    });
    wind?.postMessage(JSON.stringify(data), "*");
    window.parent.postMessage(JSON.stringify(message));
  }

  consoleLog(["LOG IN CHECK LOGIN TOKEN COrrect", loginData?.jwtToken || loginData, loginData?.jwtToken, loginData]);
  setItemInLocalStorage("_zenvoya_JWT", loginData?.jwtToken || loginData);
  setAccessJWT(loginData?.jwtToken || loginData);
  return decodedToken;
}

//  check log in and making API call
export async function checkLogin({
  sendPixelEvent,
  setIsValidAccessToken,
  setAccessJWT,
  accessJWT,
  email,
  loginType,
  name,
  password,
  setUserInfo,
  setIsLoggedIn,
  signInClick,
  setLoginCreateAccountState,
  loginCreateAccountState,
  setSignInClick,
  loginFailureMessage,
  utmParams,
  setPasswordError,
}: any) {
  const payLoad = {
    email: email?.trim() || "",
    loginType: loginType,
    name: name?.trim() || "",
    password: password?.trim() || "",
    utmParams: utmParams || "",
  };
  const response: LoginResponse = await login(accessJWT, payLoad);
  consoleLog(["LOG IN CHECK LOGIN", response]);
  if (response?.message === "Email id does not exists") {
    setLoginCreateAccountState({
      ...loginCreateAccountState,
      page: DialogSelected.CREATE_ACCOUNT,
    });
    toastMessage.error("OOPS!", "Email id not registered, please signup.");
    return null;
  }
  if (response?.message === "Wrong password" && setPasswordError) {
    toastMessage.error("OOPS!", "Wrong password");
    setPasswordError(Messages["login.failure"].message);
    return null;
  }
  // Email verification gate: the account is unverified and email verification is
  // on. The backend has already sent an OTP; hand the caller the signal so it can
  // drive the verify step (no session issued). Only occurs for email login.
  if ((response as any)?.data?.needsVerification) {
    return {
      needsVerification: true,
      userId: (response as any)?.data?.userId,
      email,
    };
  }
  if (response?.message === "This account must sign in through the administrator portal.") {
    toastMessage.error("OOPS!", response.message);
    if (setPasswordError) setPasswordError(response.message);
    return null;
  }
  if (response?.message !== "Login successfull") {
    toastMessage.error("Sorry!", "We were unable to log you in. Please try again.");
    return null;
  }

  if (response?.message === "Login successfull") {
    const checkExistingUser = response?.data?.existingUser;
    consoleLog(["LOG IN GOOGLE check exist", response, checkExistingUser]);
    if (!checkExistingUser) {
      if (loginType === "Google") {
        triggerGAEvent(GA_LABEL_ENUMS.signup_complete_google, "", "", "", {
          full_name: name,
          email: email,
          traffic_source: utmParams?.match(/utm_source=([^&]+)/)?.[1] || "",
        });
        sendPixelEvent("signUp", { method: GA_LABEL_ENUMS.signup_complete_google, email: email });
      } else if (loginType === "Apple") {
        triggerGAEvent(GA_LABEL_ENUMS.signup_complete_apple, "", "", "", {
          full_name: name,
          email: email,
          traffic_source: utmParams?.match(/utm_source=([^&]+)/)?.[1] || "",
        });
        sendPixelEvent("signUp", { method: GA_LABEL_ENUMS.signup_complete_apple, email: email });
      } else if (loginType === "Facebook") {
        triggerGAEvent(GA_LABEL_ENUMS.signup_complete_facebook, "", "", "", {
          full_name: name,
          email: email,
          traffic_source: utmParams?.match(/utm_source=([^&]+)/)?.[1] || "",
        });
        sendPixelEvent("signUp", { method: GA_LABEL_ENUMS.signup_complete_facebook, email: email });
      }
      consoleLog("New User Signup completed - GA event triggered");
    }
    const decodedToken = storeLoginSession(response?.data, {
      setIsValidAccessToken,
      setUserInfo,
      setIsLoggedIn,
      setAccessJWT,
    });
    const mintedToken = response?.data?.jwtToken || response?.data;

    if (signInClick && checkExistingUser) {
      // setLoginCreateAccountState({ ...loginCreateAccountState, flag: false });
      setSignInClick(false);
    } else if (checkExistingUser) {
      // setLoginCreateAccountState({
      //   ...loginCreateAccountState,
      //   flag: false,
      //   page: "",
      // });
    }

    return {
      id: decodedToken.id,
      isExistingUser: response?.data?.existingUser,
      jwt: mintedToken,
    };
  } else {
    toastMessage.error("OOPS", loginFailureMessage);
    return null;
  }
}

// Get Local Storage Data
export function getLocalStorageData(localItem: string, setItem?: any) {
  const localStorageData = getItemFromLocalStorage(localItem);
  if (localStorageData && setItem) {
    setItem(JSON.parse(localStorageData));
  }

  return localStorageData;
}

// Get Local Storage Data
export function getSessionStorage(localItem: string, setItem: any) {
  const localSessionData = window.sessionStorage.getItem(localItem);
  if (localSessionData) {
    setItem(JSON.parse(localSessionData));
  }

  return localSessionData;
}
